import { Chip, CircularProgress, Stack, Tooltip } from '@mui/material';
import type React from 'react';
import { useEffect, useMemo } from 'react';

import { AccountBox, Recycling } from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CategoryIcon from '@mui/icons-material/Category';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import ViewListIcon from '@mui/icons-material/ViewList';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { usePageStore } from '@/Components/templates/PageStore';
import type { RouterIds } from '@/Router';
import { useZnglyContext } from '@/Utility/context/ZnglyAppContext';
import { getTextFromHtmlString } from '@/Utility/utils';
import { duplicateContentMutation } from '@/graphql/mutations/Content.mutation';
import type { ZnglyContext_Backend_Query } from '@/graphql/queries/ZnglyContext.query';
import {
  ZnglyVideosPaginationFragment,
  type ZnglyVideos_Pagination_Fragment$data,
  type ZnglyVideos_Pagination_Fragment$key,
} from '@/graphql/queries/ZnglyVideo.query';
import { usePaginationFragment } from '@/graphql/relay.utils';
import { MediaItemSizeEnum, PostStatusEnum } from '@/graphql/types';
import { type RelayStore, useRelayStore } from '@/graphql/useRelayStore';
import type { CatalogFilterConfigType } from '@/routes/dash/catalog';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import NoSearchFound from '../NoSearchFound';
import Suspense from '../Suspense';
import type { AddFromCatalogFilterConfigType } from '../addFromCatalog';
import MultiViewPicker from '../multiViewPicker/MultiViewPicker';
import type { ListChildType, ListViewProps } from '../multiViewPicker/views';
import MoreDialog, { type MoreDialogItem } from '../multiViewPicker/views/grid/MoreDialog/MoreDialog';
import { getIsStatus, getStatusTypeObject } from '../posts/status';
import { getCatalogMediaFeaturedImage, mediaTypeNameHandler } from '../posts/utils/catalog';

// @todo: why is this the css import? makes no sense why znglist css is being used for catalog items
// Refactor and figure correct format for this
import '@/routes/dash/znglists/Znglists.less';

type RelayData = ReturnType<
  typeof usePaginationFragment<ZnglyContext_Backend_Query, ZnglyVideos_Pagination_Fragment$key>
>;

type Props = {
  view?: 'items' | 'select';
  style?: React.CSSProperties;
  className?: string;
  defaultWhereArgs?: RelayStore['ZnglyVideos_Pagination_Fragment']['__paginationVariables']['where'];
  filterOptions: AddFromCatalogFilterConfigType | CatalogFilterConfigType;
};

type ZnglyContentProps = Props & {
  isFallback?: boolean;
} & Partial<RelayData>;

const ZnglyContents = ({ className, view, isFallback, filterOptions, ...props }: ZnglyContentProps) => {
  const navigate = useNavigate();

  const filters = filterOptions.useFilters();

  // Determine if trash filter is active directly from filters (no hook)
  const isTrashFilter = useMemo(() => {
    const visibility = filters.visibility as PostStatusEnum[] | undefined;
    return visibility?.length === 1 && visibility[0] === PostStatusEnum.Trash;
  }, [filters.visibility]);

  const znglyVideos: NonNullable<ZnglyVideos_Pagination_Fragment$data['znglyVideos']>['edges'][number]['node'][] =
    useMemo(() => {
      if (isFallback && !props?.data?.znglyVideos?.edges) {
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        return [...Array.from({ length: 6 }, () => ({ id: '', title: '' }) as any)];
      }
      if (!props?.data?.znglyVideos?.edges) return [];

      return props?.data?.znglyVideos?.edges?.filter((edge) => !!edge?.node?.id).map(({ node }) => node);
    }, [props?.data?.znglyVideos?.edges, isFallback]);

  const loadMoreHandler = () => {
    if (!props?.hasNext || props?.isLoadingNext) return;

    props?.loadNext?.(24);
  };

  const moreDialogProps = (node: (typeof znglyVideos)[number]) => {
    return [
      {
        label: 'Edit',
        action: () => {
          navigate({
            to: '/dash/content/$editId',
            params: {
              editId: node?.id,
            },
          });
        },
        icon: <EditIcon />,
      },
      !getIsStatus(node?.status, PostStatusEnum.Trash) && {
        label: 'Duplicate',
        location: 'dropdown-menu',
        icon: <FileCopyIcon />,
        action: () => {
          const connectionIds = useRelayStore.getState().ZnglyVideos_Pagination_Fragment.__id;
          usePageStore.setState({ pageLoading: true });
          duplicateContentMutation(
            {
              connections: connectionIds,
              input: {
                id: node?.id,
                node: {
                  title: `${node?.title} (Copy)`,
                },
              },
            },
            {
              onCompleted(response, errors) {
                usePageStore.setState({ pageLoading: false });
                if (!!errors?.length || !response?.duplicateZnglyVideo?.znglyVideo?.id) {
                  toast.error(`Failed to duplicate Catalog: ${errors?.map((err) => err.message).join(', ')}`);
                  return;
                }

                navigate({
                  to: '/dash/content/$editId',
                  params: {
                    editId: response?.duplicateZnglyVideo?.znglyVideo?.id,
                  },
                });
              },
            }
          );
        },
      },
      !getIsStatus(node?.status, [PostStatusEnum.Trash, PostStatusEnum.Draft]) && {
        label: 'View',
        action: () => {
          // Type safety: by using a template literal type, we ensure that the URL is always in the correct format
          const url: RouterIds = '/content/$postId';
          const newUrl = url.replace('$postId', node?.id);

          window.open(newUrl, '_blank');
        },
        icon: <VisibilityIcon />,
      },
    ] as MoreDialogItem[];
  };

  const getListProps = (node: (typeof znglyVideos)[number]): ListChildType => {
    if (isFallback) {
      return [];
    }
    const imgUrl = getCatalogMediaFeaturedImage(node?.znglyContent, MediaItemSizeEnum.Large) ?? '';

    return [
      {
        child: <img key={node?.id} src={imgUrl} />,
        component: 'th',
        scope: 'row',
      },
      {
        child: (
          <div>
            <a key={node?.id} href={node?.link ?? ''} style={{ fontSize: '14px' }}>
              {node?.title}
            </a>
            {(node?.znglyContent?.htmlDesc || node?.znglyContent?.shortText) && (
              <Tooltip
                title={getTextFromHtmlString(node.znglyContent.htmlDesc || node.znglyContent.shortText).text}
                enterDelay={1000}
              >
                <p
                  style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.54)',
                  }}
                >
                  {getTextFromHtmlString(node.znglyContent.htmlDesc || node.znglyContent.shortText).text}
                </p>
              </Tooltip>
            )}
          </div>
        ),
        style: {
          width: '30%',
        },
      },
      mediaTypeNameHandler(node?.znglyContent?.contentMediaName),
      <Chip
        key={node?.id}
        className={`visibility-${node?.status?.toLowerCase()}`}
        label={getStatusTypeObject(node?.statusSettings)?.label}
      />,
      node?.author?.node?.name,
      node?.categories?.edges[0]?.node?.name || 'No Category',
      node?.date?.slice?.(0, 10) ?? '',
      isTrashFilter && node?.trashTimeUntil,
      <MoreDialog key={node?.id} items={moreDialogProps(node)} />,
    ].filter(Boolean) as ListChildType;
  };

  const NoContent = () => {
    if (znglyVideos?.length === 0) {
      return <NoSearchFound />;
    }
  };

  return (
    <div className={`zngly-contents ${className ?? ''}`} style={{ margin: '1em auto' }}>
      <NoContent />

      <MultiViewPicker
        type="catalog"
        isFallback={isFallback}
        data={znglyVideos}
        loadMore={loadMoreHandler}
        gridSelectView={view === 'select'}
        gridProps={(node) => {
          const status = getStatusTypeObject(node?.statusSettings);

          return {
            id: node?.databaseId?.toString(),
            header: node?.title ?? '',
            headerLink: node?.link ?? '#',
            subheader: getTextFromHtmlString(node?.znglyContent?.htmlDesc ?? node?.znglyContent?.shortText ?? '').text,
            imageUrl: getCatalogMediaFeaturedImage(node?.znglyContent, MediaItemSizeEnum.Large) ?? '',
            menuItems: moreDialogProps(node),
            useHoverButtons: true,
            gridDetails: [
              {
                icon: <AccountBox />,
                label: node?.author?.node?.name,
                tooltip: `Author: ${node?.author?.node?.name}`,
              },
              {
                icon: status?.icon,
                label: status?.label,
                tooltip: `Visibility: ${status?.label}`,
              },
              {
                icon: <PermMediaIcon />,
                label: mediaTypeNameHandler(node?.znglyContent?.contentMediaName),
                tooltip: `Media type: ${mediaTypeNameHandler(node?.znglyContent?.contentMediaName)}`,
              },
              {
                icon: <CalendarMonthIcon />,
                label: node?.date?.slice(0, 10),
                tooltip: `Publish date: ${node?.date?.slice(0, 10)}`,
              },
              {
                icon: <CategoryIcon />,
                label: node?.categories?.edges?.[0]?.node?.name ?? (isFallback ? null : 'No Category'),
                tooltip: !!node?.categories?.edges?.[0]
                  ? `Category: ${node?.categories?.edges?.[0]?.node?.name}`
                  : isFallback
                    ? null
                    : 'No Category',
              },
              {
                icon: <ViewListIcon />,
                label: node.znglyContent?.form?.node?.content ?? (isFallback ? null : 'No Form'),
              },
              getIsStatus(node?.status, PostStatusEnum.Trash) && {
                icon: <Recycling />,
                label: node?.trashTimeUntil,
                tooltip: `Will be deleted in ${node?.trashTimeUntil}`,
              },
            ].filter(Boolean),
          };
        }}
        tableProps={{
          headerProps: [
            {
              value: 'Image',
              width: 140,
              style: { textAlign: 'center' },
            },
            {
              value: 'Title & Description',
            },
            {
              value: 'Media',
            },
            {
              value: 'Visibility',
            },
            {
              value: 'Author',
            },
            {
              value: 'Category',
            },
            {
              value: 'Publish Date',
            },
            isTrashFilter && {
              value: 'Scheduled Deletion',
            },
            {
              value: null,
              align: 'center',
            },
          ].filter(Boolean) as ListViewProps<'catalog'>['headerProps'],
          listProps: (item) => getListProps(item),
        }}
      />

      {props?.isLoadingNext && (
        <Stack direction="row" justifyContent="center" sx={{ padding: 2 }}>
          <CircularProgress />
        </Stack>
      )}
    </div>
  );
};

const QueryLoader = (props: Props) => {
  const { backendData } = useZnglyContext();
  const filters = props.filterOptions.useFilters();

  const paginationData = usePaginationFragment<ZnglyContext_Backend_Query, ZnglyVideos_Pagination_Fragment$key>(
    ZnglyVideosPaginationFragment,
    backendData?.queryRef!
  );

  // Effect to handle filter changes
  useEffect(() => {
    const params = filters;
    let visibility = (params.visibility as PostStatusEnum[]) ?? [];

    if (props?.view === 'items' && !visibility?.length) {
      visibility = [
        PostStatusEnum.Pending,
        PostStatusEnum.Publish,
        PostStatusEnum.Hidden,
        PostStatusEnum.HiddenBS,
        PostStatusEnum.Draft,
      ];
    }

    if (props?.view === 'select') {
      const allowedStati = [PostStatusEnum.Publish, PostStatusEnum.Hidden, PostStatusEnum.HiddenBS];
      if (!visibility?.length) visibility = allowedStati;
      else visibility = visibility.filter((status) => allowedStati.includes(status?.toUpperCase() as PostStatusEnum));
    }

    paginationData.refetch(
      {
        first: 24,
        after: null,
        where: {
          search: params.search ?? null,
          orderby: params.order ? [params.order] : null,
          stati: visibility?.length ? visibility : null,
          mediaName: params.mediaName?.length ? params.mediaName : null,
          authorIn: params.author?.length ? params.author : null,
          categoryIn: props.view !== 'select' && params.category?.length ? params.category : null,
          tagIn: params.tags?.length ? params.tags : null,
          dateQuery: params.date ? { ...params.date, inclusive: true } : null,

          /**
           * Override current variables with default variables
           */
          ...props?.defaultWhereArgs,
        },
      },
      {
        fetchPolicy: 'store-or-network',
      }
    );
  }, [filters, props.defaultWhereArgs, props.view, paginationData.refetch]);

  return <ZnglyContents {...props} {...paginationData} />;
};

const ZnglyContentsSuspense = (props: Props) => {
  const { backendData } = useZnglyContext();

  return (
    <Suspense
      fallback={<ZnglyContents isFallback filterOptions={props.filterOptions} />}
      exists={backendData?.queryRef}
    >
      <QueryLoader {...props} />
    </Suspense>
  );
};

export default ZnglyContentsSuspense;
